import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { openDownload } from "@/lib/downloads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  ScanLine, ArrowLeft, Camera, X, Loader2, AlertCircle, Warehouse, Truck, User as UserIcon,
  ArrowRightLeft, CheckCircle2, Search,
} from "lucide-react";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";

const SCANNER_ID = "equipment-qr-reader";

export default function EquipmentScan() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scannerRef = useRef(null);
  const resolvedRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [resolved, setResolved] = useState(null);
  const [manualToken, setManualToken] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [error, setError] = useState(null);
  const [autoResolving, setAutoResolving] = useState(false);

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch {
        // ignore stop errors
      }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  const extractToken = (decodedText) => {
    // If decoded value is a URL with ?t=, extract just the token
    try {
      if (decodedText.includes("?t=")) {
        const url = new URL(decodedText.startsWith("http") ? decodedText : `https://x${decodedText}`);
        const t = url.searchParams.get("t");
        if (t) return t;
      }
    } catch {
      // fall through
    }
    return decodedText;
  };

  const onScanSuccess = async (decodedText) => {
    await stopScanner();
    const token = extractToken(decodedText);
    await resolveToken(token);
  };

  const explainError = (err) => {
    const name = (err && err.name) || "";
    const msg = ((err && err.message) || "").toLowerCase();
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent || "");
    const isSecure = window.isSecureContext;

    if (!isSecure) {
      return "Camera requires HTTPS. Open this app via the https:// URL (not http) and try again.";
    }
    if (name === "NotAllowedError" || msg.includes("permission") || msg.includes("denied") || msg.includes("notallowed")) {
      return isIOS
        ? "Camera permission denied. On iPhone: open Settings → Safari → Camera → set to Ask or Allow. Then close all Safari tabs, reopen this page and tap 'Open Camera'. Tip: also close FaceTime/WhatsApp if they're using the camera."
        : "Camera permission denied. Click the padlock/camera icon in your browser's address bar and Allow camera, then reload and try again.";
    }
    if (name === "NotFoundError" || msg.includes("not found") || msg.includes("notfound")) {
      return "No camera detected on this device. Use the manual token input below, or open this page on a phone.";
    }
    if (name === "NotReadableError" || msg.includes("in use") || msg.includes("notreadable")) {
      return "Camera is busy — another app (FaceTime, WhatsApp, Zoom) is using it. Close that app and tap 'Open Camera' again.";
    }
    if (name === "OverconstrainedError" || msg.includes("overconstrained")) {
      return "No rear camera found matching the requested settings. Try again — we'll fall back to any available camera.";
    }
    if (name === "SecurityError") {
      return "Camera blocked by browser security settings. Ensure the page is served over HTTPS and permissions are allowed.";
    }
    return `Could not start camera (${err?.message?.slice(0, 100) || "unknown error"}). Use the manual input below.`;
  };

  const startScanner = async () => {
    setError(null);
    setResolved(null);

    // 1) Guard: browser support + secure context
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError("Your browser does not support camera access. Use Safari (iOS) or Chrome (Android) via HTTPS.");
      return;
    }
    if (!window.isSecureContext) {
      setError("Camera requires HTTPS. Open this app via the https:// URL.");
      return;
    }

    // 2) Explicitly request permission from a user gesture — this is the
    // most reliable way to trigger the iOS Safari permission prompt.
    // Once granted, we stop this probe stream and hand off to Html5Qrcode
    // using a specific deviceId (more stable than facingMode on iOS).
    let probeStream = null;
    try {
      probeStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });
    } catch (permErr) {
      // Retry with any camera if rear-facing is over-constrained on this device
      try {
        probeStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      } catch (permErr2) {
        setError(explainError(permErr2 || permErr));
        return;
      }
    } finally {
      // Always stop the probe stream immediately so Html5Qrcode can own the camera
      if (probeStream) {
        try { probeStream.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
      }
    }

    // 3) Enumerate cameras and prefer the rear-facing one by deviceId
    let cameraId = null;
    try {
      const cams = await Html5Qrcode.getCameras();
      if (cams && cams.length > 0) {
        const rear = cams.find((c) => /back|rear|environment/i.test(c.label || "")) || cams[cams.length - 1];
        cameraId = rear.id;
      }
    } catch {
      // If enumeration fails, fall through to facingMode
    }

    // 4) Start Html5Qrcode with deviceId (preferred) or facingMode fallback
    const config = { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 };
    try {
      const html5 = new Html5Qrcode(SCANNER_ID);
      scannerRef.current = html5;
      setScanning(true);
      const source = cameraId ? cameraId : { facingMode: "environment" };
      await html5.start(source, config, onScanSuccess, () => {});
    } catch (startErr) {
      // Last-resort fallback: any camera
      try {
        const html5 = new Html5Qrcode(SCANNER_ID);
        scannerRef.current = html5;
        setScanning(true);
        await html5.start({ facingMode: "user" }, config, onScanSuccess, () => {});
      } catch (finalErr) {
        setError(explainError(finalErr));
        setScanning(false);
        scannerRef.current = null;
      }
    }
  };

  const resolveToken = async (token) => {
    try {
      const r = await api.get(`/equipment/qr-resolve/${encodeURIComponent(token.trim())}`);
      setResolved(r.data);
      toast.success(`Found: ${r.data.name}`);
    } catch (e) {
      setError("QR code not recognized in your inventory.");
      toast.error("QR not recognized");
    }
  };

  // Auto-resolve when URL has ?t=TOKEN (deep-link from phone camera)
  useEffect(() => {
    const t = searchParams.get("t");
    if (t && !resolved && !autoResolving) {
      setAutoResolving(true);
      resolveToken(t).finally(() => setAutoResolving(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const onManualResolve = async () => {
    if (!manualToken.trim()) return;
    setError(null);
    setResolved(null);
    // Support pasting a full deep-link URL — extract just the token
    await resolveToken(extractToken(manualToken.trim()));
  };

  const doSearch = async (q) => {
    if (!q.trim()) { setSearchResults([]); return; }
    try {
      const r = await api.get(`/equipment/search`, { params: { q: q.trim() } });
      setSearchResults(r.data);
    } catch {
      setSearchResults([]);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => doSearch(searchQ), 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  useEffect(() => {
    if (resolved && resolvedRef.current) {
      // Bring the identified equipment card into view (helpful when user
      // arrived here from a native phone camera deep-link `?t=TOKEN`).
      resolvedRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [resolved]);

  useEffect(() => {
    return () => { stopScanner(); };
    // eslint-disable-next-line
  }, []);

  return (
    <div className="space-y-5 max-w-2xl mx-auto" data-testid="equipment-scan-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/equipment")} className="text-slate-400 hover:text-blue-300" data-testid="scan-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ScanLine className="w-6 h-6 text-blue-400" /> Scan Equipment QR
          </h1>
          <p className="text-slate-400 text-xs mt-1">
            Point your phone camera at the QR sticker — or scan from your phone&apos;s default camera app (the URL will deep-link here).
          </p>
        </div>
      </div>

      {autoResolving && (
        <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-4 text-center flex items-center justify-center gap-2" data-testid="auto-resolving">
          <Loader2 className="w-4 h-4 animate-spin text-blue-300" /> <span className="text-sm text-slate-300">Resolving QR token...</span>
        </div>
      )}

      {/* Camera scanner */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-4">
        {scanning ? (
          <div>
            <div id={SCANNER_ID} className="rounded-lg overflow-hidden bg-black aspect-square max-w-md mx-auto" />
            <Button onClick={stopScanner} variant="outline" className="border-rose-500/30 text-rose-300 mt-3 w-full" data-testid="stop-scanner-btn">
              <X className="w-4 h-4 mr-2" /> Stop Camera
            </Button>
          </div>
        ) : (
          <div className="text-center py-6">
            <Camera className="w-12 h-12 text-blue-400 mx-auto mb-3" />
            <p className="text-slate-300 mb-4">Tap to start camera and scan a QR code</p>
            <Button onClick={startScanner} className="bg-blue-600 hover:bg-blue-500" data-testid="start-scanner-btn">
              <ScanLine className="w-4 h-4 mr-2" /> Open Camera
            </Button>
          </div>
        )}
        {error && (
          <div className="mt-3 rounded-md bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-xs text-rose-300 flex items-start gap-2" data-testid="scan-error">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
          </div>
        )}
      </div>

      {/* Manual token entry */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-4">
        <Label className="text-xs uppercase tracking-wider text-slate-400 font-bold">Or paste QR token manually</Label>
        <div className="flex gap-2 mt-1.5">
          <Input
            value={manualToken} onChange={(e) => setManualToken(e.target.value)}
            placeholder="QR token from sticker"
            className="bg-[#030712] border-slate-800 font-mono"
            data-testid="manual-token-input"
          />
          <Button onClick={onManualResolve} disabled={!manualToken.trim()} className="bg-blue-600 hover:bg-blue-500" data-testid="manual-resolve-btn">
            Resolve
          </Button>
        </div>
      </div>

      {/* Quick search */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-4">
        <Label className="text-xs uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1.5">
          <Search className="w-3.5 h-3.5" /> Quick Search (no QR)
        </Label>
        <Input
          value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
          placeholder="Search by part number / serial / name"
          className="mt-1.5 bg-[#030712] border-slate-800"
          data-testid="scan-search-input"
        />
        {searchResults.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {searchResults.map((it) => (
              <button
                key={it.id}
                onClick={() => navigate(`/equipment/${it.id}`)}
                className="w-full text-left rounded-md border border-slate-800 bg-slate-900/40 hover:border-blue-500/40 hover:bg-blue-500/5 p-2.5"
                data-testid={`search-result-${it.part_number}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-100 truncate">{it.name}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{it.part_number}</div>
                  </div>
                  <LocationBadge location={it.location} technicianName={it.technician_name} />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Result */}
      {resolved && (
        <div ref={resolvedRef}>
          <ResolvedCard item={resolved} onMoved={() => { setResolved(null); toast.success("Updated"); }} navigate={navigate} />
        </div>
      )}
    </div>
  );
}

function LocationBadge({ location, technicianName }) {
  const conf = {
    storage: { color: "bg-slate-800 border-slate-700 text-slate-300", icon: Warehouse, label: "Storage" },
    van: { color: "bg-amber-500/10 border-amber-500/30 text-amber-300", icon: Truck, label: "Van" },
    technician: { color: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300", icon: UserIcon, label: technicianName || "Tech" },
  }[location] || { color: "bg-slate-800 text-slate-400", icon: AlertCircle, label: location };
  const Icon = conf.icon;
  return (
    <Badge className={`${conf.color} flex items-center gap-1 shrink-0 text-[10px]`}>
      <Icon className="w-3 h-3" /> {conf.label}
    </Badge>
  );
}

function ResolvedCard({ item, onMoved, navigate }) {
  const [to, setTo] = useState(item.location === "storage" ? "van" : "storage");
  const [techName, setTechName] = useState("");
  const [techId, setTechId] = useState("");
  const [reason, setReason] = useState("checkout");
  const [busy, setBusy] = useState(false);
  const [technicians, setTechnicians] = useState([]);

  useEffect(() => {
    api.get("/master/technicians").then((r) => setTechnicians(r.data)).catch(() => {});
  }, []);

  const move = async () => {
    if (to === "technician" && !techName) {
      toast.error("Pick a technician");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/equipment/${item.id}/movements`, {
        to_location: to,
        to_technician_id: techId || null,
        to_technician_name: techName || null,
        reason,
      });
      const formUrl = r.data.form_url;
      toast.success("Movement saved · opening custody form");
      if (formUrl) {
        // Iter 47: mint short-lived ticket instead of embedding JWT
        const m = formUrl.match(/equipment\/([^/]+)\/movements\/([^/]+)/);
        if (m) {
          await openDownload(formUrl.replace(/^\/api/, "/api"), "equipment_detail_pdf", `${m[1]}:mv:${m[2]}`);
        } else {
          const { buildDownloadUrl } = await import("@/lib/downloads");
          const url = await buildDownloadUrl(formUrl.replace(/^\/api/, "/api"), "equipment_detail_pdf", "unknown");
          window.open(url, "_blank");
        }
      }
      onMoved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-4 space-y-4" data-testid="scan-result-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-blue-300 font-bold flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" /> Found in inventory
          </div>
          <div className="text-lg font-bold text-white mt-1">{item.name}</div>
          <div className="text-xs text-slate-400 font-mono">{item.part_number}{item.serial_number ? ` · SN ${item.serial_number}` : ""}</div>
          {item.brand && <div className="text-xs text-slate-500 mt-1">{item.brand} {item.model || ""}</div>}
        </div>
        <LocationBadge location={item.location} technicianName={item.technician_name} />
      </div>

      <div className="rounded-md bg-slate-900/40 border border-slate-800 p-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">Current Location</div>
        <div className="text-sm text-slate-200">
          {item.location === "technician"
            ? <>In hands of <span className="text-emerald-300 font-medium">{item.technician_name || "(unassigned)"}</span></>
            : item.location === "van"
            ? <>Inside the <span className="text-amber-300 font-medium">Van</span></>
            : <>At the <span className="text-slate-200 font-medium">Storage</span></>}
        </div>
      </div>

      {/* Quick move */}
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1.5">
          <ArrowRightLeft className="w-3.5 h-3.5" /> Move it now
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select value={to} onChange={(e) => setTo(e.target.value)}
            className="h-10 px-3 rounded-md bg-[#030712] border border-slate-800 text-sm text-slate-200"
            data-testid="scan-move-to">
            <option value="storage">→ Storage</option>
            <option value="van">→ Van</option>
            <option value="technician">→ Technician</option>
          </select>
          <select value={reason} onChange={(e) => setReason(e.target.value)}
            className="h-10 px-3 rounded-md bg-[#030712] border border-slate-800 text-sm text-slate-200"
            data-testid="scan-move-reason">
            <option value="checkout">Checkout</option>
            <option value="return">Return</option>
            <option value="transfer">Transfer</option>
            <option value="service">Service</option>
            <option value="lost">Lost</option>
          </select>
        </div>
        {to === "technician" && (
          <select value={techName}
            onChange={(e) => {
              const name = e.target.value;
              setTechName(name);
              const t = technicians.find((x) => x.name === name);
              setTechId(t?.id || "");
            }}
            className="w-full h-10 px-3 rounded-md bg-[#030712] border border-slate-800 text-sm text-slate-200"
            data-testid="scan-move-technician">
            <option value="">— Select technician —</option>
            {technicians.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
          </select>
        )}
        <div className="flex gap-2">
          <Button onClick={() => navigate(`/equipment/${item.id}`)}
            variant="outline" className="border-slate-700 flex-1"
            data-testid="scan-open-detail">
            Open Full Detail
          </Button>
          <Button onClick={move} disabled={busy} className="bg-blue-600 hover:bg-blue-500 flex-1" data-testid="scan-move-confirm">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ArrowRightLeft className="w-3.5 h-3.5 mr-1.5" />}
            Move & Print
          </Button>
        </div>
      </div>
    </div>
  );
}
